import React, { useState } from 'react';
import { styled } from '@mui/system';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import MenuUpsellItem from 'base-ordering/components/MenuUpsellItem';
import { MENU_ITEM_NAMES } from 'styles/constants/appearanceSectionNames';
import { DEFAULT_MENU_UPSELL_HEADER } from 'bento-ordering/cart/constants/copies';
import muiPropForwarding from 'styles/helpers/muiPropForwarding';
import MenuItemNameTypography from 'components/MenuItemNameTypography';
import { CartBorder } from 'bento-ordering/base/components/CartBorder';
import { getUpsellBackgroundColor } from 'styles/helpers/getThemeStyles';
import { useIsMobile } from 'styles/hooks/mediaQueries';

import QuickAddToCart from 'base-ordering/components/QuickAddToCartButton';
import { QUICK_ADD_TO_CART_TIMEOUT } from 'base-ordering/constants/index';

const bentoStyles = isMobile => ({
  padding: `0rem ${!isMobile ? 1 : 2}rem`,

  // This is to make the upsell container take full width of container with padding
  margin: `0px ${!isMobile ? -20 : -36}px`
});

const cateringStyles = () => ({
  padding: '0rem 1rem'
});

const StyledUpsellContainer = styled('div', {
  shouldForwardProp: muiPropForwarding(['isMobile', 'isBentoOrdering'])
})(({ theme, isMobile, isBentoOrdering }) => ({
  background: getUpsellBackgroundColor(theme),
  ...(isBentoOrdering ? bentoStyles(isMobile) : cateringStyles())
}));

const StyledUpsellsBox = styled('div')({
  paddingTop: '1px'
});

export const StyledUpsellsMenuItemContainer = styled('div')({
  flexGrow: 1
});

const StyledHeaderText = styled(MenuItemNameTypography)({
  fontSize: '20px',
  margin: '16px 0'
});

const StyledQuickAddUpsellContainer = styled(Box, {
  shouldForwardProp: muiPropForwarding('isLastItem')
})(({ isLastItem }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: isLastItem ? '40px' : '10px'
}));

const MenuUpsellContainer = ({
  isUpsellEnabled,
  upsellItems,
  handleOpenModal,
  handleQuickAddToCart = null,
  menuUpsellHeader = DEFAULT_MENU_UPSELL_HEADER
}) => {
  const [fadeForQuickAddId, setFadeForQuickAddId] = useState(null);
  const isMobile = useIsMobile();

  return (
    <StyledUpsellContainer
      isMobile={isMobile}
      isBentoOrdering={!!handleQuickAddToCart}
    >
      {isUpsellEnabled && !!upsellItems.length && (
        <Box sx={{ paddingBottom: '1rem' }}>
          <Box position="relative">
            <CartBorder />
          </Box>
          <StyledUpsellsBox>
            <StyledHeaderText component="h2" variant={MENU_ITEM_NAMES}>
              {menuUpsellHeader}
            </StyledHeaderText>
            {upsellItems.map((menuItem, idx) => {
              const isLastItem = idx === upsellItems.length - 1;

              return (
                <Fade
                  key={menuItem.id}
                  in={menuItem.id !== fadeForQuickAddId}
                  timeout={{ exit: QUICK_ADD_TO_CART_TIMEOUT }}
                  unmountOnExit
                >
                  <StyledQuickAddUpsellContainer isLastItem={isLastItem}>
                    <StyledUpsellsMenuItemContainer
                      onClick={() => handleOpenModal(menuItem.product.slug)}
                      isLastItem={isLastItem}
                    >
                      <MenuUpsellItem
                        name={menuItem.product.name}
                        images={menuItem.product.images}
                        defaultPrice={menuItem.product.default_price}
                      />
                    </StyledUpsellsMenuItemContainer>
                    {handleQuickAddToCart && (
                      <QuickAddToCart
                        upsellItem={menuItem}
                        setFade={() => setFadeForQuickAddId(menuItem.id)}
                        handleAddToCart={() =>
                          handleOpenModal(menuItem.product.slug)
                        }
                        handleQuickAddToCart={upsellItem => {
                          handleQuickAddToCart(upsellItem);
                        }}
                      />
                    )}
                  </StyledQuickAddUpsellContainer>
                </Fade>
              );
            })}
          </StyledUpsellsBox>
        </Box>
      )}
    </StyledUpsellContainer>
  );
};

MenuUpsellContainer.propTypes = {
  isUpsellEnabled: PropTypes.bool.isRequired,
  upsellItems: PropTypes.array.isRequired,
  handleOpenModal: PropTypes.func.isRequired,
  handleQuickAddToCart: PropTypes.func,
  menuUpsellHeader: PropTypes.string
};

export default MenuUpsellContainer;
